/**
 * seatRoles.ts — per-seat logins Phase 7: the frontend consumes the role source of truth.
 *
 * D1: Clerk `publicMetadata.role` is the role SoT (backend-written only).
 * D8: `unsafeMetadata` is browser-writable by the signed-in user, so it is never a role
 *     source once REACT_APP_SEAT_ROLE_SOT is on.
 * D7: canonical ids — FE `accountant_internal` / `accountant_external`, BE `accountant`;
 *     seat `neutral` ↔ role `mediator`.
 *
 * REACT_APP_SEAT_ROLE_SOT is a build-time env (vite `define`, Dockerfile ARG). Unset or any
 * value other than true/1/yes/on = off = the pre-Phase-7 behavior.
 */

export function parseSeatRoleSotFlag(raw: unknown): boolean {
  const v = String(raw ?? '').trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

export function isSeatRoleSotEnabled(): boolean {
  return parseSeatRoleSotFlag(process.env.REACT_APP_SEAT_ROLE_SOT);
}

/**
 * Display names, one place. OPEN OWNER QUESTION (CANONICAL_NAMES_AUDIT 2026-10-23): the two
 * accountant names are proposals ("Forensic Accountant" / "Independent CPA"). Change them here.
 */
export const SEAT_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  owner: 'Owner',
  admin: 'Admin',
  analyst: 'Analyst',
  viewer: 'Viewer',
  data_specialist: 'Data Specialist',
  accountant_internal: 'Forensic Accountant',
  accountant_external: 'Independent CPA',
  attorney: 'Attorney',
  attorney_internal: 'On-Staff Attorney',
  mediator: 'Neutral / Mediator',
  public_adjuster: 'Public Adjuster',
  carrier_adjuster: 'Carrier Adjuster',
  client: 'Client',
};

export function seatDisplayName(id: string | null | undefined): string {
  if (!id) return '—';
  return SEAT_DISPLAY_NAMES[id] ?? id;
}

export type ProvisionableSeatId =
  | 'accountant_internal'
  | 'data_specialist'
  | 'attorney'
  | 'mediator'
  | 'public_adjuster'
  | 'carrier_adjuster'
  | 'admin';

export interface ProvisionableSeat {
  id: ProvisionableSeatId;
  /** law_firms.org_type the seat must be provisioned into; null = staff seat (no firm). */
  orgType: 'firm' | 'mediator' | 'public_adjuster' | 'carrier' | null;
  /** The backend refuses this seat (403) unless the caller is the owner. */
  ownerOnly: boolean;
}

/** Mirrors the Phase 5 backend SEATS table. owner / adjuster / accountant_external are refused there. */
export const PROVISIONABLE_SEATS: ReadonlyArray<ProvisionableSeat> = [
  { id: 'accountant_internal', orgType: null, ownerOnly: false },
  { id: 'data_specialist', orgType: null, ownerOnly: false },
  { id: 'attorney', orgType: 'firm', ownerOnly: false },
  { id: 'mediator', orgType: 'mediator', ownerOnly: false },
  { id: 'public_adjuster', orgType: 'public_adjuster', ownerOnly: false },
  { id: 'carrier_adjuster', orgType: 'carrier', ownerOnly: false },
  { id: 'admin', orgType: null, ownerOnly: true },
];
